import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    TextField,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showsAutocomplete } from '../autocomplete';
import AutocompleteSelect from '../components/atom/AutoCompleteSelect';
import { getTimeslots } from '@openapi/timeslots/timeslots';
import { addBooking } from '@openapi/bookings/bookings';
import { useGetShow } from '@openapi/shows/shows';
import useNotify from '../hooks/useNotify';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useCapabilities } from '../hooks/useCapabilities';
import { BOOKING_STATUSES } from '../helpers/constants';
import { getSlotTemplate } from '../helpers/slotTemplate';
import TimeslotCalendar from '../components/TimeslotCalendar';

function AddBookingModal({ showModal, MODAL_TYPE }) {
    const { user } = useAuth();
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const navigate = useNavigate();
    const { can, CAPABILITIES } = useCapabilities();
    const canAssignSeats = can(CAPABILITIES.SEATING_ASSIGNMENTS);
    const [step, setStep] = useState(1); // 1: Select Show, 2: Select Timeslot
    const [selectedShow, setSelectedShow] = useState(null);
    const [timeslots, setTimeslots] = useState([]);
    const [loadingTimeslots, setLoadingTimeslots] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const autoSelectDoneRef = useRef(false);
    const { data: selectedShowFull } = useGetShow(Number(selectedShow?.id), {
        query: { enabled: !!selectedShow?.id },
    });
    const delayedSeatAssignmentEnabled =
        !!selectedShowFull?.marketing?.delayedSeatAssignmentEnabled;

    const loadTimeslots = async (showId) => {
        setLoadingTimeslots(true);
        try {
            const response = await getTimeslots({
                pageSize: 10000,
                optionalId: showId,
                sort: 'starttime',
                sortDir: 'ASC',
                includeBlocks: true,
            });
            setTimeslots(response.timeslots || []);
        } catch (error) {
            notifyError(error);
            setTimeslots([]);
        } finally {
            setLoadingTimeslots(false);
        }
    };

    const handleShowSelect = (show) => {
        autoSelectDoneRef.current = true;
        setSelectedShow(show);
        if (show) {
            setStep(2);
            loadTimeslots(show.id);
        }
    };

    useEffect(() => {
        let mounted = true;
        showsAutocomplete
            .search('', 2)
            .then((opts) => {
                if (!mounted || autoSelectDoneRef.current) return;
                autoSelectDoneRef.current = true;
                if (opts.length === 1) {
                    handleShowSelect(opts[0]);
                }
            })
            .catch((err) => {
                console.error('Default show lookup failed', err);
            });
        return () => {
            mounted = false;
        };
    }, []);

    const handleBack = () => {
        setStep(1);
        setSelectedShow(null);
        setTimeslots([]);
    };

    const onSubmit = async (timeslotId) => {
        if (!selectedShow || !timeslotId || submitting) return;
        setSubmitting(true);
        try {
            const res = await addBooking({
                // default "empty" booking — backend derives venueId from the timeslot
                status: BOOKING_STATUSES.PROCESSING,
                timeslotId: parseInt(timeslotId, 10),
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                note: [],
                checkinNote: '',
                agentId: user.id, // agentId is the current user
            });
            navigate(`/bookings/edit/${res.id}`);
            notifySuccess(t('bookings.notifications.added'));
            showModal(MODAL_TYPE.NONE);
        } catch (error) {
            notifyError(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            aria-labelledby="add-booking-modal"
            open
            maxWidth="lg"
            fullWidth
            onClose={() => showModal(MODAL_TYPE.NONE)}
        >
            <DialogTitle>
                {step === 1
                    ? t('bookings.newBooking')
                    : `${selectedShow?.name} - ${t('timeslots.title')}`}
            </DialogTitle>

            <DialogContent>
                {step === 1 ? (
                    // Step 1: Select Show
                    <Grid2 container>
                        <Grid2 size={{ xs: 12 }}>
                            <AutocompleteSelect
                                initCall={showsAutocomplete.init}
                                autocompleteCall={showsAutocomplete.search}
                                value={selectedShow?.id ?? null}
                                setValue={(showId, getShowInfo) => {
                                    handleShowSelect(getShowInfo(showId));
                                }}
                            >
                                <TextField label={t('shows.title')} required />
                            </AutocompleteSelect>
                        </Grid2>
                    </Grid2>
                ) : (
                    // Step 2: Select Timeslot (Calendar view)
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12 }}>
                            {loadingTimeslots ? (
                                <Box display="flex" justifyContent="center" p={3}>
                                    <CircularProgress />
                                </Box>
                            ) : timeslots.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    textAlign="center"
                                    p={3}
                                >
                                    {t('tickettypes.noTimeslots')}
                                </Typography>
                            ) : (
                                <TimeslotCalendar
                                    timeslots={timeslots}
                                    disabled={submitting}
                                    disableCancelledSelection
                                    onTimeslotSelect={onSubmit}
                                    onSeatAssign={
                                        canAssignSeats && delayedSeatAssignmentEnabled
                                            ? (timeslotId) => {
                                                  showModal(MODAL_TYPE.NONE);
                                                  navigate(
                                                      `/shows/${selectedShow.id}/timeslots/${timeslotId}/assign-seats`,
                                                  );
                                              }
                                            : undefined
                                    }
                                    slotTemplate={getSlotTemplate(selectedShowFull)}
                                />
                            )}
                        </Grid2>
                    </Grid2>
                )}
            </DialogContent>

            <DialogActions>
                <CommonButton
                    variant="inherit"
                    onClick={() => showModal(MODAL_TYPE.NONE)}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
                {step === 2 && (
                    <CommonButton
                        variant="inherit"
                        onClick={handleBack}
                        label={t('common.back')}
                        buttonType="secondary"
                    />
                )}
            </DialogActions>
        </Dialog>
    );
}

export default AddBookingModal;
